import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_ZONES_RESOURCE } from 'src/features/zone/api/useListZones';
import { useUpdateZone } from 'src/features/zone/api/useUpdateZone';
import ZoneFormModal from 'src/features/zone/components/form/ZoneFormModal';
import { Zone } from 'src/features/zone/utils/types';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface ZoneFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZone: Zone;
    children?: React.ReactNode;
}

function UpdateZoneFormModal<TData>({ className, semantics, children, selectedZone }: ZoneFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateZone({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ZONES_RESOURCE] });
            toast({
                title: formatMessage(zoneMessages.updateZoneSuccess),
                description: formatMessage(zoneMessages.updateZoneSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <ZoneFormModal
            selectedZone={selectedZone}
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ data, zoneId: selectedZone.id, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(zoneMessages.editZoneSubtitle, {
                entry: selectedZone.translations.find((translation) => translation.language === locale)?.name,
            })}
        >
            {children}
        </ZoneFormModal>
    );
}

export default UpdateZoneFormModal;
